import React, { Suspense, useRef, useState, useEffect } from 'react';
// eslint-disable-next-line no-unused-vars -- only TooltipProvider is used
import { TooltipProvider } from './ui/tooltip.tsx';
import { Card, CardContent } from './ui/card.tsx';
import { Input } from './ui/input.tsx';
import { Label } from './ui/label.tsx';
import { Button } from './ui/button.tsx';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from 'firebase/auth';
import ForgotPasswordDialog from './ForgotPasswordDialog';
import CheckboxField from './forms/CheckboxField';
import { clearLastActivityAt, getStoredRememberMe, getStoredRememberMeEmail, REMEMBER_ME_KEY, setLastActivityAt, setStoredRememberMeEmail } from '../constants/sessionTimeout';
import { useIdleSessionTimeout } from '../hooks/useIdleSessionTimeout';
import { useAppUpdateChecker, reloadForUpdate, consumeResumeState, isNewBuildDeployed } from '../hooks/useAppUpdateChecker';
import { getClientRecord } from '../services/firestoreService';
import { prefetchLiveClients } from '../services/liveClientsStore';
import { ENVELOPES_SCOPE_CLIENT, writeEnvelopesScopeMode } from '../utils/envelopeAccountRows';
import { consumeWorkflowDeepLink } from '../utils/workflowDeepLink';
import { consumeGuideDeepLink } from '../utils/guideDeepLink';
import { useClientState, ClientScopeContext, clientStore, getActiveClientScope } from '../services/clientStateService';
import clientStateService from '../services/clientStateService';
import { useWorkspaceTabs, MAX_WORKSPACE_TABS, readPersistedWorkspaceTabs, clearPersistedWorkspaceTabs } from '../hooks/useWorkspaceTabs';
import { useWorkspaceTabShortcuts } from '../hooks/useWorkspaceTabShortcuts';
import { WorkspaceTabsContext } from '../contexts/WorkspaceTabsContext';
import WorkspaceTabStrip from './header/WorkspaceTabStrip';
import WorkspaceTabSwitcher from './header/WorkspaceTabSwitcher';

import { ALL_ADMIN_TAB_NAMES, getFirstAccessibleAdminTab, LEGACY_ADMIN_TAB } from '../constants/adminPages';
import { APP_NAVIGATE_EVENT, navigateTo } from '../utils/appNavigation';


import { PAGE_IDS, REPORTS_PAGE_IDS } from '../constants/pageVisibility';
import { getDisplayAppName } from '../utils/domainConfig';
import userTeamService from '../services/userTeamService';
import { syncMyTimeZone } from '../services/userTimeZoneService';
import { AppHeaderContext } from '../contexts/AppHeaderContext';
import { getNavTreeForUser, getPageLabel, getSectionTitle, isSectionInSidebar, SUB_PAGE_PARENT, FACT_FIND_SECTIONS, REVERSE_FACT_FIND_SECTIONS, getDefaultTabForViewAsRole, TABS_REQUIRING_CLIENT, RETIRED_TAB_REDIRECTS } from '../config/navConfig';
import { getDefaultTabIdForPage, getNavTabsForPage, isAdviceTabGroupPage, withAdviceTabSectionsHidden } from '../config/adviceTabGroups';
import useAdviceWizardLayout from '../hooks/useAdviceWizardLayout';
import { LEGACY_ADVISORY_PORTAL_TABS, LEGACY_GA_PORTAL_TAB } from '../constants/advisoryPortalConfig';
import { useFactFindSectionOrder } from '../hooks/useFactFindSectionOrder';
import AppSidebar, { SidebarProvider, SidebarInset, SidebarTrigger } from './AppSidebar';
import { ContextMenuProvider } from '../contexts/ContextMenuContext';
import {
  Breadcrumb,
  CollapsingBreadcrumbList,
  BreadcrumbLink,
  BreadcrumbPage,
} from './ui/breadcrumb.tsx';
import CallWidget from './CallWidget';
import { useMyAssignedOverdueCount } from '../hooks/useMyAssignedOverdueCount';
import { usePendingFileNotesCount } from '../hooks/usePendingFileNotesCount';
import { resolveHomePortalTab } from '../utils/homePortalTab';
import { useMediaQuery } from '../hooks/use-mobile.tsx';
import { useTeamMissedCallUnreadCount } from '../hooks/useTeamMissedCalls';
import AppLoadingScreen from './AppLoadingScreen';
import SettingsLayout from './settings/SettingsLayout';
import { getFirstAccessibleSettingsTab, getSettingsEntryForTab, isSettingsTab } from '../config/settingsNav';
import { guardedNavigate } from '../utils/leaveGuards';
import { HeaderBannerRow } from './HeaderBannerTicker';
import ResearchLibraryBanner from './ResearchLibraryBanner';
import { VoiceCallProvider, useVoiceCall } from '../contexts/VoiceCallContext';
import { Toaster, toast } from 'sonner';
import DocumentWarningPrompt from './DocumentWarningPrompt';
import { useTeamSmsUnreadCount } from '../hooks/useTeamSmsUnreadCount';
import { useUnreadCount } from '../hooks/mail/useUnreadCount';
import { useSmsUnreadBrowserAlerts } from '../hooks/useSmsUnreadBrowserAlerts';
import { useCommsNotificationPermission } from '../hooks/useCommsNotificationPermission';
import { useInboundSmsOsNotifications } from '../hooks/useInboundSmsOsNotifications';
import SupportChatWidget from './support-chat/SupportChatWidget';
import ParaButton from './para/ParaButton';
import { lazyPage } from '../utils/lazyPage';

// Pages load on first visit (their code is split out of the main bundle), so
// the app starts without downloading every page — slow on phones.
const FactFind = lazyPage(() => import('./FactFind'));
const Advice = lazyPage(() => import('./Advice'));
const GuidedAdvicePage = lazyPage(() => import('./guidedAdvice/GuidedAdvicePage'));
const FileNotePage = lazyPage(() => import('./FileNotePage'));
const FileNotesPage = lazyPage(() => import('./FileNotesPage'));
const MessagesPage = lazyPage(() => import('./MessagesPage'));
const AmendmentsPage = lazyPage(() => import('./AmendmentsPage'));
const DocumentStoragePage = lazyPage(() => import('./DocumentStoragePage'));
const ESignaturesPage = lazyPage(() => import('./ESignaturesPage'));
const importSendEnvelopePage = () => import('./SendEnvelopePage');
const SendEnvelopePage = lazyPage(importSendEnvelopePage);
const AdminFormConfigPage = lazyPage(() => import('./admin/pages/AdminFormConfigPage'));
const AdminPortfoliosPage = lazyPage(() => import('./admin/pages/AdminPortfoliosPage'));
const AdminAiPromptsPage = lazyPage(() => import('./admin/pages/AdminAiPromptsPage'));
const AdminBannerPage = lazyPage(() => import('./admin/pages/AdminBannerPage'));
const AdminMarketingImportsPage = lazyPage(() => import('./admin/pages/AdminMarketingImportsPage'));
const AdminDeploymentsPage = lazyPage(() => import('./admin/pages/AdminDeploymentsPage'));
const AdminDocumentsPage = lazyPage(() => import('./admin/pages/AdminDocumentsPage'));
const AdminClientAllocationPage = lazyPage(() => import('./admin/pages/AdminClientAllocationPage'));
const AdminGuidedSandboxPage = lazyPage(() => import('./admin/pages/AdminGuidedSandboxPage'));
const AdminSettingsInCodePage = lazyPage(() => import('./admin/pages/AdminSettingsInCodePage'));
const AdminWorkflowsPage = lazyPage(() => import('./admin/pages/AdminWorkflowsPage'));
const importAdvisoryPortal = () => import('./AdvisoryPortal');
const AdvisoryPortal = lazyPage(importAdvisoryPortal);
const importComplianceDashboardPage = () => import('./compliance/ComplianceDashboardPage');
const ComplianceDashboardPage = lazyPage(importComplianceDashboardPage);
const importParaplannerPortal = () => import('./ParaplannerPortal');
const ParaplannerPortal = lazyPage(importParaplannerPortal);
const CsvMergePortal = lazyPage(() => import('./CsvMergePortal'));
const CalendarPage = lazyPage(() => import('./CalendarPage'));
const EmailPage = lazyPage(() => import('./email/EmailPage'));
const AuditLog = lazyPage(() => import('./AuditLog'));
const ReverseFactFindPage = lazyPage(() => import('./ReverseFactFindPage'));
const ClientOverviewPage = lazyPage(() => import('./ClientOverviewPage'));
const AdviceDatesPage = lazyPage(() => import('./AdviceDatesPage'));
const PortalTasksPanel = lazyPage(() => import('./PortalTasksPanel'));
const FAQsPage = lazyPage(() => import('./FAQsPage'));
const GuidesPage = lazyPage(() => import('./GuidesPage'));
const HelpDeskPage = lazyPage(() => import('./HelpDeskPage'));
const MarketingReportPage = lazyPage(() => import('./reports/MarketingReportPage'));
const ParaplanningReportPage = lazyPage(() => import('./reports/ParaplanningReportPage'));
const AdviceApprovalsReportPage = lazyPage(() => import('./reports/AdviceApprovalsReportPage'));
const ImplementationReportPage = lazyPage(() => import('./reports/ImplementationReportPage'));
const SignedDocumentsReportPage = lazyPage(() => import('./reports/SignedDocumentsReportPage'));
const FsgEmailsReportPage = lazyPage(() => import('./reports/FsgEmailsReportPage'));
const UnconvertedReportPage = lazyPage(() => import('./reports/UnconvertedReportPage'));
const PeopleAccessSettingsPage = lazyPage(() => import('./settings/pages/PeopleAccessSettingsPage'));
const MessagesInboxPage = lazyPage(() => import('./messages/MessagesInboxPage'));
const BulkSmsPage = lazyPage(() => import('./messages/BulkSmsPage'));
const CommunicationsSettingsPage = lazyPage(() => import('./settings/pages/CommunicationsSettingsPage'));
const ClientOverridesSettingsPage = lazyPage(() => import('./settings/pages/ClientOverridesSettingsPage'));

// Each role's home page (getDefaultTabForUser). The role is only known once the
// teams/users load finishes, so sign-in warms the home page this browser last
// used, and the first workspace tab starts on it.
const HOME_PAGE_IMPORTERS = {
  advisoryPortal: importAdvisoryPortal,
  paraplannerPortal: importParaplannerPortal,
  sendEnvelope: importSendEnvelopePage,
  complianceDashboard: importComplianceDashboardPage,
};
const LAST_HOME_PAGE_KEY = 'advicelink:last-home-page';

function readLastHomePage() {
  let page = null;
  try { page = window.localStorage.getItem(LAST_HOME_PAGE_KEY); } catch { /* storage blocked */ }
  return HOME_PAGE_IMPORTERS[page] ? page : 'advisoryPortal';
}

function rememberHomePage(page) {
  if (!HOME_PAGE_IMPORTERS[page]) return;
  try { window.localStorage.setItem(LAST_HOME_PAGE_KEY, page); } catch { /* storage blocked */ }
}

function preloadHomePortal() {
  // The page's own lazy load reports (and recovers from) a failed chunk.
  HOME_PAGE_IMPORTERS[readLastHomePage()]().catch(() => {});
}

/** Shown in the content area while a page's code downloads on first visit. */
function PageLoadingState() {
  return (
    <div className="flex h-full min-h-[40vh] w-full items-center justify-center gap-2 text-sm text-muted-foreground" role="status" aria-live="polite">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground" aria-hidden />
      Loading…
    </div>
  );
}

function CommsNotificationSetup({ user, enabled }) {
  const { voiceAvailable } = useVoiceCall();
  useCommsNotificationPermission(user, { voiceAvailable, enabled });
  useInboundSmsOsNotifications(user, { enabled });
  return null;
}

const ADMIN_TAB_SET = new Set([...ALL_ADMIN_TAB_NAMES, LEGACY_ADMIN_TAB]);
const REPORTS_TAB_SET = new Set(REPORTS_PAGE_IDS);
const REPORTS_PAGE_COMPONENTS = {
  [PAGE_IDS.REPORTS_MARKETING]: MarketingReportPage,
  [PAGE_IDS.REPORTS_PARAPLANNING]: ParaplanningReportPage,
  [PAGE_IDS.REPORTS_ADVICE_APPROVALS]: AdviceApprovalsReportPage,
  [PAGE_IDS.REPORTS_IMPLEMENTATION]: ImplementationReportPage,
  [PAGE_IDS.REPORTS_SIGNED_DOCUMENTS]: SignedDocumentsReportPage,
  [PAGE_IDS.REPORTS_FSG_EMAILS]: FsgEmailsReportPage,
  [PAGE_IDS.REPORTS_UNCONVERTED]: UnconvertedReportPage,
};
function renderReportsPage(tab, user) {
  const Page = REPORTS_PAGE_COMPONENTS[tab];
  return Page ? <Page user={user} /> : null;
}

/**
 * Settings pages (rendered inside the Settings hub). Merged entries render one
 * page for several ids (each id is that page's tab); every id keeps its own
 * permission. ctx: { canAccess, onNavigate }.
 */
const renderAdminPage = (activeTab, user, ctx = {}) => {
  const merged = { user, activeTab, canAccess: ctx.canAccess || (() => true), onNavigate: ctx.onNavigate };
  switch (activeTab) {
    case 'adminTeams':
    case 'adminPageVisibility':
    case 'adminDefaultFilters':
      return <PeopleAccessSettingsPage {...merged} />;
    case 'adminCorrespondence':
    case 'adminSmsNumbers':
    case 'adminIdUploadSms':
    case 'adminSmsScheduling':
    case 'adminSystemEmails':
    case 'adminDocusignSettings':
      return <CommunicationsSettingsPage {...merged} />;
    case 'adminManualOverride':
    case 'adminParaplannerOverride':
    case 'adminClientJson':
      return <ClientOverridesSettingsPage {...merged} />;
    case 'adminDocuments':
    case 'adminDocumentQa':
    case 'adminArDocumentTest':
      return <AdminDocumentsPage {...merged} />;
    // Layout and its shared-wording editor (and, Oct 23, the Templates tab, Chart Settings and Styling) were retired in Oct 2026; old links open Documents.
    case 'adminDocumentStyling':
    case 'adminChartSettings':
    case 'adminDocxTemplates':
    case 'adminContentBlocks':
    case 'adminDocumentLayout':
      return <AdminDocumentsPage {...merged} />;
    case 'adminFormConfig':
      return <AdminFormConfigPage user={user} />;
    case 'adminPortfolios':
      return <AdminPortfoliosPage user={user} />;
    case 'adminAiPrompts':
      return <AdminAiPromptsPage user={user} />;
    case 'adminBanner':
      return <AdminBannerPage user={user} />;
    case 'adminMarketingImports':
      return <AdminMarketingImportsPage user={user} />;
    case 'adminDeployments':
      return <AdminDeploymentsPage user={user} />;
    case 'adminWorkflows':
      return <AdminWorkflowsPage user={user} />;
    case 'adminClientAllocation':
      return <AdminClientAllocationPage user={user} />;
    case 'adminGuidedSandbox':
      return <AdminGuidedSandboxPage user={user} />;
    // Assumptions moved to code on Oct 23 2026; its page id opens Settings in code.
    case 'adminAssumptions':
    case 'adminSettingsInCode':
      return <AdminSettingsInCodePage />;
    default:
      return null;
  }
};

const VIEW_AS_KEYS = ['ga', 'paraplanner', 'paraplannerManagement', 'adviser', 'management', 'ufSupport', 'arSupport', 'arAdviser', 'compliance'];
const VIEW_AS_STORAGE_KEY = 'advicelink_viewAsRole';

/**
 * One-shot guard so a tab reloads at most once per sign-in. Normally no loop is
 * possible (after the reload the user is signed in, so handleLogin never runs
 * again), but this stops a hard lock if the session somehow fails to restore and
 * the user re-enters their credentials.
 */
const LOGIN_RELOAD_GUARD_KEY = 'advicelink.loginReload';

/**
 * After signing in, pick up an overnight deploy before the user starts working:
 * the login screen itself may be running a stale bundle, and signing in is not
 * an in-app navigation, so nothing else would trigger the reload.
 */
async function shouldReloadAfterLogin() {
  try {
    if (sessionStorage.getItem(LOGIN_RELOAD_GUARD_KEY)) return false;
  } catch {
    // Storage unavailable — fall through and check anyway.
  }
  return isNewBuildDeployed();
}

function markLoginReloaded() {
  try {
    sessionStorage.setItem(LOGIN_RELOAD_GUARD_KEY, '1');
  } catch {
    // Storage unavailable — the reload still happens, just unguarded.
  }
}

function clearLoginReloadGuard() {
  try {
    sessionStorage.removeItem(LOGIN_RELOAD_GUARD_KEY);
  } catch {
    // Storage unavailable — nothing to clear.
  }
}

function getViewAsFromURL() {
  if (typeof window === 'undefined') return null;
  const fromUrl = new URLSearchParams(window.location.search).get('viewAs');
  if (VIEW_AS_KEYS.includes(fromUrl)) return fromUrl;
  const fromStorage = sessionStorage.getItem(VIEW_AS_STORAGE_KEY);
  return VIEW_AS_KEYS.includes(fromStorage) ? fromStorage : null;
}

function AdviceJourneyTab({ user }) {
  const { selectedClient, clientData } = useClientState();
  const activeClient = clientData || selectedClient;

  if (!selectedClient) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="w-full px-4 lg:px-6 xl:px-8 py-8">
          <Card>
            <CardContent className="p-6 text-center text-muted-foreground">
              Select a client to view their tasks.
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="w-full px-4 lg:px-6 xl:px-8 py-8">
        {/* Legacy Advice Journey retired Sept 2026: the client's tasks are workflow runs. */}
        <PortalTasksPanel user={user} clientData={activeClient} className="" />
      </div>
    </div>
  );
}

const FinancialAdviserApp = () => {
  // Theme context available but dark mode toggle removed from UI
  const { selectedClient, getClientDisplayName, clientData } = useClientState();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cacheRefreshedForUser, setCacheRefreshedForUser] = useState(false);
  const [pageVisibilityRevision, setPageVisibilityRevision] = useState(0);
  // Workspace tabs: each keeps its own page, section and client (see useWorkspaceTabs).
  // The values below are the tab in front; the setters write to it.
  const [firstHomePage] = useState(readLastHomePage);
  const workspace = useWorkspaceTabs({
    initialPage: firstHomePage,
    onLimitReached: () => toast.message(`You can have up to ${MAX_WORKSPACE_TABS} tabs open`, {
      description: 'Close a tab to open another.',
    }),
  });
  const workspaceRef = useRef(workspace);
  workspaceRef.current = workspace;
  // Tabs saved before this page load, read once at mount (before anything re-saves them).
  const savedWorkspaceTabsRef = useRef(undefined);
  if (savedWorkspaceTabsRef.current === undefined) savedWorkspaceTabsRef.current = readPersistedWorkspaceTabs();
  const { updateTab: updateWorkspaceTab, getActiveTabId } = workspace;
  const activeWorkspaceTab = workspace.activeTabRecord;
  const activeTab = activeWorkspaceTab.page;
  const activeSection = activeWorkspaceTab.section;
  // Per tab too: scrollToSectionId (set only on sidebar section click so pages scroll without
  // snap-on-scroll) and portalSearchPrefill (one-shot: client-badge click seeds the portal search).
  const documentStorageBreadcrumbPath = activeWorkspaceTab.documentStorageBreadcrumbPath;
  // Setters for the tab in front (resolved at call time, so async callers follow a switch).
  const [setActiveTab, setActiveSection, setScrollToSectionId, setPortalSearchPrefill, setSettingsMenuRequest] = React.useMemo(() => {
    const setterFor = (key) => (value) => updateWorkspaceTab(getActiveTabId(), (t) => ({
      [key]: typeof value === 'function' ? value(t[key]) : value,
    }));
    return ['page', 'section', 'scrollToSectionId', 'portalSearchPrefill', 'settingsMenuRequest'].map(setterFor);
  }, [updateWorkspaceTab, getActiveTabId]);
  // Per-tab setters handed to that tab's page (stable per tab id).
  const tabSettersRef = useRef(new Map());
  const getTabSetters = (tabId) => {
    let setters = tabSettersRef.current.get(tabId);
    if (!setters) {
      const setField = (key) => (value) => updateWorkspaceTab(tabId, (t) => ({
        [key]: typeof value === 'function' ? value(t[key]) : value,
      }));
      setters = {
        setSection: setField('section'),
        setDocumentStorageBreadcrumbPath: setField('documentStorageBreadcrumbPath'),
        onScrollRequestFulfilled: () => updateWorkspaceTab(tabId, { scrollToSectionId: null }),
        onPortalSearchPrefillConsumed: () => updateWorkspaceTab(tabId, { portalSearchPrefill: null }),
      };
      tabSettersRef.current.set(tabId, setters);
    }
    return setters;
  };
  const [loginForm, setLoginForm] = useState(() => ({
    email: getStoredRememberMeEmail(),
    password: '',
  }));
  const [loginError, setLoginError] = useState('');
  const [loginSubmitting, setLoginSubmitting] = useState(false);
  const [rememberMe, setRememberMe] = useState(getStoredRememberMe);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);

  const [factFindSections, setFactFindSections] = useState([]); // Admin-configured order for Fact Find sidebar

  // Marketers can hold a team line so they can send the secure ID/TFN upload
  // link, but they have no inbox page. Badges open Calls & Messages, so only
  // count for people who can open it — otherwise a lit badge navigates them to
  // a tab they are not allowed to see.
  const canOpenSmsInbox = Boolean(user?.email)
    && userTeamService.canAccessTab(user.email, 'messagesInbox');

  const smsUnreadCount = useTeamSmsUnreadCount(user, {
    enabled: cacheRefreshedForUser && canOpenSmsInbox,
  });
  useSmsUnreadBrowserAlerts(smsUnreadCount);
  // Calls & Messages sidebar badge (the portal panels that showed these were removed).
  const missedCallUnreadCount = useTeamMissedCallUnreadCount(user, {
    enabled: Boolean(cacheRefreshedForUser && canOpenSmsInbox),
  });
  // Home portal sidebar badge: overdue tasks + pending file notes (were header badges).
  const portalCountsEnabled = Boolean(cacheRefreshedForUser && user?.email);
  const overdueTaskCount = useMyAssignedOverdueCount(user, { enabled: portalCountsEnabled });
  const pendingFileNotesCount = usePendingFileNotesCount(user, { enabled: portalCountsEnabled });
  useIdleSessionTimeout(user);
  const { updateAvailable } = useAppUpdateChecker({
    enabled: Boolean(user),
    getResumeState: () => ({
      tab: activeTab,
      section: activeSection,
      clientId: selectedClient?.id ?? null,
    }),
  });

  // When admin has ?viewAs=ga|paraplanner|adviser|ufSupport|arSupport|arAdviser, show that role's nav and portal
  const viewAsRole = (user?.email && userTeamService.getUserRole(user.email) === 'super_admin') ? getViewAsFromURL() : null;
  // Saved tabs belong to one view-as mode (switching mode reloads the app).
  workspace.setViewAs(viewAsRole);

  // Route guard: Check if user can access a tab
  const canUserAccessTab = (tabName) => {
    if (!user?.email) return false;
    return userTeamService.canAccessTab(user.email, tabName);
  };

  // Default tab per team (portal they should see when they log in)
  const getDefaultTabForUser = (authUser) => {
    if (!authUser?.email) return 'advisoryPortal';
    const role = userTeamService.getUserRole(authUser.email);
    const accessibleTabs = userTeamService.getAccessibleTabs(authUser.email);
    let roleDefault = {
      advisory: 'advisoryPortal',
      paraplanner: 'paraplannerPortal',
      marketing: 'sendEnvelope',
      super_admin: 'advisoryPortal',
    }[role || ''];
    if (userTeamService.getUserConfig(authUser.email)?.advisoryRole === 'compliance') {
      roleDefault = 'complianceDashboard';
    }
    const defaultTab = roleDefault || 'advisoryPortal';
    // A stored Page Visibility list can still hold a retired id (e.g. clientMeeting); it is never home.
    const normalizedTabs = accessibleTabs.map((t) =>
      LEGACY_ADVISORY_PORTAL_TABS.includes(t) || t === LEGACY_GA_PORTAL_TAB ? 'advisoryPortal' : t
    ).filter((t) => !RETIRED_TAB_REDIRECTS[t]);
    return normalizedTabs.includes(defaultTab) ? defaultTab : (normalizedTabs[0] || 'advisoryPortal');
  };

  // Portal the client badge links back to: role default when it's a searchable portal,
  // otherwise the first searchable portal the user (or viewed-as role) can access.
  const getClientBadgePortalTab = () => {
    const searchablePortals = ['advisoryPortal', 'paraplannerPortal'];
    const accessibleTabs = viewAsRole
      ? userTeamService.getAccessibleTabsForRole(viewAsRole)
      : (user ? userTeamService.getAccessibleTabs(user.email) : []);
    const normalizedTabs = accessibleTabs.map((t) =>
      LEGACY_ADVISORY_PORTAL_TABS.includes(t) || t === LEGACY_GA_PORTAL_TAB ? 'advisoryPortal' : t
    );
    const roleDefault = getDefaultTabForUser(user);
    if (searchablePortals.includes(roleDefault) && normalizedTabs.includes(roleDefault)) return roleDefault;
    return searchablePortals.find((t) => normalizedTabs.includes(t)) || null;
  };

  // Whether a page is allowed for the current user (including sub-pages) — restored tabs are checked with this.
  const canUserAccessPage = (page) => {
    if (!user?.email || !page) return false;
    if (viewAsRole) {
      const tabs = userTeamService.getAccessibleTabsForRole(viewAsRole);
      const parent = SUB_PAGE_PARENT[page];
      const tabToCheck = parent || page;
      return tabs.includes(tabToCheck);
    }
    const parent = SUB_PAGE_PARENT[page];
    if (parent) return canUserAccessTab(parent) || userTeamService.getUserRole(user.email) === 'super_admin';
    return canUserAccessTab(page);
  };

  // Tabs that require a client to be selected (otherwise show "No Client Selected")
  const tabRequiresClient = (tabName) => TABS_REQUIRING_CLIENT.includes(tabName);
  const tabDisabledNoClient = (tabName) => tabRequiresClient(tabName) && !selectedClient;

  // Keep every open client (one per workspace tab, shared by tabs on the same client) in
  // sync with Firestore when another user/session updates it (e.g. implementation
  // confirm → waitingForAR) while client tools are open.
  const openClientIdsKey = clientStore.getOpenClientIds().sort().join(',');
  useEffect(() => {
    if (!user?.uid || !openClientIdsKey) return undefined;
    const unsubs = openClientIdsKey.split(',').map((clientId) => onSnapshot(
      doc(db, 'clients', clientId),
      (snap) => {
        if (!snap.exists()) return;
        const c = { id: snap.id, ...snap.data() };
        clientStateService.applyServerClientDocument(c);
      },
      (err) => console.error('Client document subscription error:', err)
    ));
    return () => {
      unsubs.forEach((unsub) => unsub && unsub());
    };
  }, [user?.uid, openClientIdsKey]);

  // A tab's client is not tab state: save tab locations when the front tab's client changes.
  useEffect(() => {
    workspace.persistNow();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- persistNow is stable
  }, [selectedClient?.id]);

  // Navigation from sidebar: set tab and optional section. Only set scrollToSectionId when user clicks a section (so page scrolls once); scroll-reported activeSection does not trigger scroll.
  // Retired pages: Advice Cycle -> the user's portal Tasks section (legacy
  // advice-cycle tasks are worked there); Client Meeting and the pages it had
  // replaced (Oct 2026) -> the user's home page.
  const resolveRetiredTab = (tabName, sectionId) => {
    const id = tabName === 'implementationChecklist' || tabName === 'tasks' ? 'adviceJourney' : tabName;
    const redirect = RETIRED_TAB_REDIRECTS[id];
    if (!redirect) return { tabName, sectionId };
    const home = viewAsRole ? getDefaultTabForViewAsRole(viewAsRole) : getDefaultTabForUser(user);
    if (redirect.home) return { tabName: home, sectionId: null };
    // Land on the replacement only when the user can open it — otherwise go home.
    if (redirect.tab && canUserAccessPage(redirect.tab)) return { tabName: redirect.tab, sectionId: redirect.section ?? sectionId };
    if (redirect.tab) return { tabName: home, sectionId: null };
    const portal = ['advisoryPortal', 'paraplannerPortal'].includes(home) ? home : 'advisoryPortal';
    return { tabName: portal, sectionId: redirect.section };
  };

  const handleNavigate = (rawTabName, rawSectionId) => {
    const { tabName, sectionId } = resolveRetiredTab(rawTabName, rawSectionId);
    let resolvedSection = sectionId ?? null;
    if (tabName === 'reverseFactFind' && resolvedSection == null && REVERSE_FACT_FIND_SECTIONS?.length) {
      resolvedSection = REVERSE_FACT_FIND_SECTIONS[0].id;
    }
    if (isAdviceTabGroupPage(tabName) && resolvedSection == null) {
      // Re-clicking the page (the mirror layout's sidebar has no sub-items) keeps the open tab.
      resolvedSection = activeTab === tabName && activeSection ? activeSection : getDefaultTabIdForPage(tabName);
    }
    // A newer build is deployed: navigation is a safe moment to pick it up.
    // Stash the destination (and client) so the reload lands where the user was headed.
    if (updateAvailable) {
      reloadForUpdate({
        tab: tabName,
        section: resolvedSection,
        clientId: selectedClient?.id ?? null,
      });
      return;
    }
    handleTabChange(tabName);
    setActiveSection(resolvedSection);
    if (!isAdviceTabGroupPage(tabName)) {
      setScrollToSectionId(resolvedSection);
    }
  };

  // Client badge click: back to the portal with the client's name prefilled in search.
  // Client stays selected — the badge's X remains the only deselect.
  const handleClientBadgeClick = () => {
    const targetTab = getClientBadgePortalTab();
    if (!targetTab) return;
    setPortalSearchPrefill(getClientDisplayName?.() || '');
    handleNavigate(targetTab);
  };

  // Tabs this user (or the viewed-as role) can open — sidebar, Settings hub menu and merged-page tabs.
  const accessibleNavTabs = React.useMemo(() => new Set(viewAsRole
    ? userTeamService.getAccessibleTabsForRole(viewAsRole)
    : (user ? userTeamService.getAccessibleTabs(user.email) : [])),
  // eslint-disable-next-line react-hooks/exhaustive-deps -- revision bumps when visibility changes
  [viewAsRole, user?.email, cacheRefreshedForUser, pageVisibilityRevision]);
  const canAccessNavTab = React.useCallback((tab) => accessibleNavTabs.has(tab), [accessibleNavTabs]);
  const settingsEntryTab = React.useMemo(() => getFirstAccessibleSettingsTab(canAccessNavTab), [canAccessNavTab]);
  // Phones: arriving at Settings from the sidebar shows the Settings menu first
  // (setSettingsMenuRequest bumps the front tab's counter).

  // Settings → Form Configuration → Wizard layout: the mirror layout's tiles replace
  // the Recommendations / Amendments sidebar sub-items.
  const { mirror: adviceMirrorLayout } = useAdviceWizardLayout(user?.email, { enabled: Boolean(user) });

  // Recompute when user or userTeamService cache is ready so sidebar shows role-based pages.
  // When viewAsRole is set (admin viewing as another role), use that role's tabs.
  const navTree = React.useMemo(() => {
    const accessibleTabs = viewAsRole
      ? userTeamService.getAccessibleTabsForRole(viewAsRole)
      : (user ? userTeamService.getAccessibleTabs(user.email) : []);
    const tree = getNavTreeForUser(accessibleTabs);
    return withAdviceTabSectionsHidden(tree.map((page) => {
      if (page.tabName === 'factFind' && factFindSections.length > 0) {
        return { ...page, sections: factFindSections };
      }
      return page;
    }), adviceMirrorLayout);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cache + visibility revision trigger recompute after async load
  }, [user, viewAsRole, cacheRefreshedForUser, pageVisibilityRevision, factFindSections, adviceMirrorLayout]);

  // Sidebar badge for Email: unread count of the user's own Outlook inbox (realtime signal doc).
  const emailTabAccessible = React.useMemo(() => navTree.some((page) => page.tabName === 'email'), [navTree]);
  const mailUnread = useUnreadCount(user, { enabled: emailTabAccessible && Boolean(cacheRefreshedForUser) });
  const homePortalTab = resolveHomePortalTab(user?.email, viewAsRole);
  const sidebarBadgesByTab = React.useMemo(
    () => ({
      email: mailUnread,
      messagesInbox: smsUnreadCount + missedCallUnreadCount,
      [homePortalTab]: overdueTaskCount + pendingFileNotesCount,
    }),
    [mailUnread, smsUnreadCount, missedCallUnreadCount, homePortalTab, overdueTaskCount, pendingFileNotesCount],
  );
  const sidebarBadgeHintsByTab = React.useMemo(() => {
    const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
    const parts = (list) => list.filter(([n]) => n > 0).map(([n, word]) => plural(n, word)).join(', ');
    return {
      messagesInbox: parts([[smsUnreadCount, 'unread message'], [missedCallUnreadCount, 'missed call']]),
      [homePortalTab]: parts([[overdueTaskCount, 'overdue task'], [pendingFileNotesCount, 'pending file note']]),
      email: parts([[mailUnread, 'unread email']]),
    };
  }, [mailUnread, smsUnreadCount, missedCallUnreadCount, homePortalTab, overdueTaskCount, pendingFileNotesCount]);

  // Live page visibility updates (admin saves, multi-session sync)
  useEffect(() => {
    return userTeamService.subscribeToPageVisibility((revision) => {
      setPageVisibilityRevision(revision);
    });
  }, []);

  // Fact Find section order for the sidebar: global, or the selected client's
  // marketing firm override when a marketing user is viewing. Live-updates
  // when an admin reorders.
  const sectionOrderViewerRole = React.useMemo(
    () => (viewAsRole === 'ga' ? 'marketing' : userTeamService.getUserRole(user?.email)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- role comes from the user cache; recompute once it is loaded
    [user?.email, viewAsRole, cacheRefreshedForUser],
  );
  const isMarketingViewer = sectionOrderViewerRole === 'marketing';
  // Phones: the header tabs collapse into one switcher.
  const isPhoneHeader = useMediaQuery('(max-width: 639px)');
  const sectionOrderClient = clientData || selectedClient;
  const factFindSectionIds = useFactFindSectionOrder({
    viewerRole: sectionOrderViewerRole,
    marketingTeamId: sectionOrderClient?.assignment?.assignedMarketingTeam || null,
  });
  useEffect(() => {
    if (!factFindSectionIds) return;
    const sectionMap = new Map(FACT_FIND_SECTIONS.map((s) => [s.id, s]));
    const ordered = factFindSectionIds.map((id) => sectionMap.get(id)).filter(Boolean);
    if (ordered.length) setFactFindSections(ordered);
  }, [factFindSectionIds]);

  // Wrapper for setActiveTab with access control
  const handleTabChange = (tabName) => {
    if (tabName === LEGACY_GA_PORTAL_TAB) {
      handleTabChange('advisoryPortal');
      return;
    }
    if (tabName === 'implementationChecklist' || tabName === 'tasks' || RETIRED_TAB_REDIRECTS[tabName]) {
      const retired = resolveRetiredTab(tabName);
      setActiveTab(retired.tabName);
      setActiveSection(retired.sectionId ?? null);
      return;
    }
    const normalizedTab = tabName === 'arWizard' ? 'advice' : tabName;
    // Portfolio edit pages are accessible if user can access admin (they're sub-pages)
    // Also allow if user is super admin (bypass page visibility check for sub-pages)
    if (tabName === 'editRecommendedPortfolio' || tabName === 'editAlternativePortfolio') {
      const parentTab = SUB_PAGE_PARENT[tabName];
      const isSuperAdmin = userTeamService.getUserRole(user?.email) === 'super_admin';
      if ((parentTab && canUserAccessTab(parentTab)) || isSuperAdmin) {
        setActiveTab(normalizedTab);
      } else {
        setActiveTab(getDefaultTabForUser(user));
        console.warn(`Access denied to tab: ${tabName}. User ${user?.email} does not have permission.`);
      }
      return;
    }

    // The front tab's client as of now (a page may select a client and navigate in one go).
    const liveClient = getActiveClientScope().selectedClient;

    // Don't switch to tabs that require a client when none is selected
    if (tabRequiresClient(normalizedTab) && !liveClient) return;

    // Special case: Allow navigation to factFind if user has a selected client
    // This ensures newly created clients can be accessed immediately
    if (normalizedTab === 'factFind' && liveClient) {
      const hasAccess = canUserAccessTab(normalizedTab);
      // Allow if user has access OR if they created the client
      if (hasAccess || liveClient.createdBy === user?.email) {
        setActiveTab(normalizedTab);
        return;
      }
    }
    
    if (canUserAccessTab(normalizedTab)) {
      setActiveTab(normalizedTab);
      if (isAdviceTabGroupPage(normalizedTab)) {
        const validTabIds = getNavTabsForPage(normalizedTab).map((tab) => tab.id);
        setActiveSection((current) => (
          validTabIds.includes(current) ? current : getDefaultTabIdForPage(normalizedTab)
        ));
      }
    } else {
      setActiveTab(getDefaultTabForUser(user));
      console.warn(`Access denied to tab: ${normalizedTab}. User ${user?.email} does not have permission.`);
    }
  };

  // Cross-page links (e.g. AI Prompts → Documents) dispatch a navigate request.
  // Guides links (a video from a task, a copied video link) go to a Guides tab that
  // is already open, else a new tab when asked (`newTab`), else the tab in front.
  useEffect(() => {
    const onNavigate = (event) => {
      const tab = event?.detail?.tab;
      if (!tab) return;
      if (tab === 'guides' && canUserAccessTab('guides')) {
        const open = workspace.tabs.find((t) => t.page === 'guides');
        if (open) {
          workspace.switchTab(open.id);
          return;
        }
        if (event.detail.newTab && workspace.openTab({ page: 'guides' })) return;
      }
      // A section (e.g. the Fact Find's riskProfile) opens the page scrolled to it, as a sidebar section click does.
      if (event.detail.section) {
        handleNavigate(tab, event.detail.section);
        return;
      }
      handleTabChange(tab);
    };
    window.addEventListener(APP_NAVIGATE_EVENT, onNavigate);
    return () => window.removeEventListener(APP_NAVIGATE_EVENT, onNavigate);
  });

  // ---- Workspace tabs: open / close / switch
  const getHomeTab = () => (viewAsRole ? getDefaultTabForViewAsRole(viewAsRole) : getDefaultTabForUser(user));
  const openNewWorkspaceTab = () => workspace.openTab({ page: getHomeTab() });
  // Cmd/Ctrl-click, middle-click and "Open in new tab" open behind the current tab (like a
  // browser); the new chip pulses briefly so the user sees where it went.
  const [justOpenedTabId, setJustOpenedTabId] = useState(null);
  useEffect(() => {
    if (!justOpenedTabId) return undefined;
    const timer = setTimeout(() => setJustOpenedTabId(null), 1200);
    return () => clearTimeout(timer);
  }, [justOpenedTabId]);
  const openBackgroundTab = (opts) => {
    const id = workspace.openTab({ ...opts, background: true });
    if (id) setJustOpenedTabId(id);
    return id;
  };
  // Cmd/Ctrl-click, middle-click: a page in a new tab. Client pages open for the client in front.
  const openPageInNewTab = (rawTabName, rawSectionId) => {
    const { tabName, sectionId } = resolveRetiredTab(rawTabName, rawSectionId);
    const client = tabRequiresClient(tabName) ? getActiveClientScope().selectedClient : null;
    if (tabRequiresClient(tabName) && !client) return;
    if (!canUserAccessPage(tabName)) return;
    let section = sectionId ?? null;
    if (tabName === 'reverseFactFind' && section == null && REVERSE_FACT_FIND_SECTIONS?.length) {
      section = REVERSE_FACT_FIND_SECTIONS[0].id;
    }
    if (isAdviceTabGroupPage(tabName) && section == null) section = getDefaultTabIdForPage(tabName);
    const id = openBackgroundTab({ page: tabName, section, client });
    if (id && section && !isAdviceTabGroupPage(tabName)) updateWorkspaceTab(id, { scrollToSectionId: section });
  };
  // A client row's "Open in new tab": the client selected in the user's portal (Advisory
  // Portal; paraplanners' own portal), or this page when they can open neither.
  const openClientInNewTab = (client, page = null) => {
    if (!client?.id) return;
    const target = page || getClientBadgePortalTab() || activeTab;
    openBackgroundTab({ page: target, client });
  };
  const workspaceTabsValue = {
    openTab: workspace.openTab,
    openPageInNewTab,
    openClientInNewTab,
    canOpenTab: workspace.canOpenTab,
  };
  useWorkspaceTabShortcuts({
    enabled: Boolean(user && cacheRefreshedForUser),
    onNew: openNewWorkspaceTab,
    onClose: () => workspace.closeTab(workspace.activeId),
    onNext: () => workspace.cycleTab(1),
    onPrev: () => workspace.cycleTab(-1),
    onJump: (index) => { const t = workspace.tabs[index]; if (t) workspace.switchTab(t.id); },
  });

  // Calendar is not client-specific: one instance for all tabs, mounted on first visit.
  const [calendarVisited, setCalendarVisited] = useState(false);
  useEffect(() => { if (activeTab === 'calendar') setCalendarVisited(true); }, [activeTab]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (authUser) => {
      setUser(authUser);
      setLoading(false);
      if (authUser) {
        // Start the client list and the home portal's code now, alongside the
        // teams/users load below, instead of after it.
        prefetchLiveClients();
        preloadHomePortal();
      }
      if (!authUser) {
        setCacheRefreshedForUser(false);
      }

      // Clear all client state when user logs out or changes
      if (!authUser) {
        clearLastActivityAt();
        clearPersistedWorkspaceTabs();
        savedWorkspaceTabsRef.current = null;
        workspaceRef.current.resetTabs([{ page: 'advisoryPortal' }]);
        clientStateService.clearClientCache();
      }
    });
    return () => unsubscribe();
  }, []);

  // When user is set, refresh cache so getAccessibleTabs has current user (fixes first-login nav race).
  // Retries with backoff: on a dropping connection one failed read used to leave
  // "Loading your workspace…" up forever. After the retries it shows Retry / Sign out.
  const [workspaceLoadError, setWorkspaceLoadError] = useState(null);
  const [workspaceRetryNonce, setWorkspaceRetryNonce] = useState(0);
  useEffect(() => {
    if (!user?.email) return undefined;

    let cancelled = false;
    const RETRY_DELAYS_MS = [0, 1000, 2000, 4000, 8000, 15000];
    const refreshCacheForUser = async () => {
      setCacheRefreshedForUser(false);
      setWorkspaceLoadError(null);
      for (let attempt = 0; attempt < RETRY_DELAYS_MS.length; attempt += 1) {
        if (RETRY_DELAYS_MS[attempt]) await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt]));
        if (cancelled) return;
        let loaded = false;
        try {
          loaded = await userTeamService.forceRefreshCache();
        } catch (err) {
          console.error('Error refreshing user team cache:', err);
        }
        if (cancelled) return;
        if (loaded && userTeamService.hasUserConfig(user.email)) {
          setCacheRefreshedForUser(true);
          return;
        }
        if (loaded) {
          // The users list loaded but this account is not in it: retrying will not help.
          console.warn(`User ${user.email} not found in users collection; navigation cannot load.`);
          setWorkspaceLoadError({
            kind: 'missingUser',
            title: 'Your account is not set up yet',
            detail: `${user.email} is not linked to a team in Advicelink. Ask an administrator to add you, then try again.`,
          });
          return;
        }
      }
      if (!cancelled) {
        setWorkspaceLoadError({
          kind: 'offline',
          title: 'Could not load your workspace',
          detail: 'The connection to the database keeps dropping. Check your internet connection (or VPN) and try again.',
        });
      }
    };
    refreshCacheForUser();
    return () => { cancelled = true; };
  }, [user?.email, workspaceRetryNonce]);

  // Save this computer's timezone once the user's record is loaded (booking dialogs show an adviser's own time).
  useEffect(() => {
    if (!user?.email || !cacheRefreshedForUser) return;
    syncMyTimeZone(user.email);
  }, [user?.email, cacheRefreshedForUser]);

  // Coming back online while stuck: try again straight away.
  useEffect(() => {
    if (!workspaceLoadError || workspaceLoadError.kind !== 'offline') return undefined;
    const onOnline = () => setWorkspaceRetryNonce((n) => n + 1);
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [workspaceLoadError]);

  // On login (or when a super admin toggles viewAs), route to the user's role-default portal.
  // Runs once per login because cacheRefreshedForUser is reset to false on logout.
  useEffect(() => {
    if (!user?.email || !cacheRefreshedForUser) return;
    if (viewAsRole) {
      setActiveTab(getDefaultTabForViewAsRole(viewAsRole));
      return;
    }
    const homePage = getDefaultTabForUser(user);
    rememberHomePage(homePage);
    setActiveTab(homePage);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run when user or cacheRefreshedForUser changes
  }, [user?.email, cacheRefreshedForUser, viewAsRole]);

  // After a reload (refresh, or an update reload when a new build is deployed), restore
  // the workspace tabs saved in sessionStorage, re-selecting each tab's client first so
  // client-required pages are not bounced to the default portal. An update reload's
  // resume state (where the user was headed) wins for the tab that was in front.
  // Placed after the role-default effect above so the restored tabs win on the same login.
  const resumeConsumedRef = React.useRef(false);
  // True once the saved tabs are back and rendered (links that open a page wait for it,
  // or the restore would replace the tab they opened).
  const [restoreSettled, setRestoreSettled] = useState(false);
  useEffect(() => {
    if (!user?.email || !cacheRefreshedForUser) return;
    if (resumeConsumedRef.current) return;
    resumeConsumedRef.current = true;

    const resume = consumeResumeState();
    const saved = savedWorkspaceTabsRef.current;
    savedWorkspaceTabsRef.current = null;
    const savedTabs = saved && (saved.viewAs ?? null) === (viewAsRole ?? null) ? saved.tabs : null;
    if (!resume && !savedTabs) {
      setRestoreSettled(true);
      return;
    }

    const entries = savedTabs ? savedTabs.map((t) => ({ ...t })) : [{}];
    const activeIndex = savedTabs ? Math.min(saved.activeIndex || 0, entries.length - 1) : 0;
    if (resume) {
      Object.assign(entries[activeIndex], {
        page: resume.tab,
        section: resume.section ?? null,
        clientId: resume.clientId ?? null,
      });
    }

    let cancelled = false;
    const restore = async () => {
      const loaded = await Promise.all(entries.map(async (entry) => {
        if (!entry.clientId) return { ...entry, client: null };
        try {
          const result = await getClientRecord(entry.clientId);
          return { ...entry, client: result.success && result.client ? result.client : null };
        } catch (err) {
          console.error('Could not restore a tab\'s client after reload:', err);
          return { ...entry, client: null };
        }
      }));
      if (cancelled) return;
      const home = viewAsRole ? getDefaultTabForViewAsRole(viewAsRole) : getDefaultTabForUser(user);
      workspaceRef.current.resetTabs(loaded.map((entry) => {
        // Saved tabs can point at a retired page (e.g. Client Meeting, Quick Quote) —
        // restore them on the replacement (home) so the strip never shows the raw old id.
        const retired = entry.page && RETIRED_TAB_REDIRECTS[entry.page] ? resolveRetiredTab(entry.page, entry.section) : null;
        const page = retired ? retired.tabName : entry.page;
        const section = retired ? retired.sectionId ?? null : entry.section ?? null;
        const usable = page
          && canUserAccessPage(page)
          && !(tabRequiresClient(page) && !entry.client);
        return {
          page: usable ? page : home,
          section: usable ? section : null,
          client: entry.client,
        };
      }), activeIndex);
    };
    restore().then(() => setRestoreSettled(true));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot restore once login state is ready
  }, [user?.email, cacheRefreshedForUser]);

  // Workflow email links: ?client=<id>[&workflowTask=<taskId>][&tab=<tab>] — open
  // the client (and tab) once login state is ready; the task modal is opened by
  // the client's workflow section when its tasks arrive.
  const deepLinkConsumedRef = useRef(false);
  useEffect(() => {
    if (!user?.email || !cacheRefreshedForUser) return;
    if (deepLinkConsumedRef.current) return;
    deepLinkConsumedRef.current = true;
    const link = consumeWorkflowDeepLink();
    if (!link.clientId) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await getClientRecord(link.clientId);
        if (cancelled || !result.success || !result.client) return;
        await clientStateService.selectClient(result.client);
        if (cancelled) return;
        // Envelope links open the Envelopes page narrowed to the linked client.
        if (link.tab === 'eSignatures') writeEnvelopesScopeMode(ENVELOPES_SCOPE_CLIENT);
        setActiveTab(link.tab || 'advisoryPortal');
      } catch (err) {
        console.error('Could not open the client from the link:', err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot once login state is ready
  }, [user?.email, cacheRefreshedForUser]);

  // Video guide links ("Copy link" under a video): ?video=<id>, plus &chapter=<key> for a
  // film — open Guides at that video (and chapter) once login state is ready and saved tabs are back.
  const guideLinkConsumedRef = useRef(false);
  useEffect(() => {
    if (!user?.email || !cacheRefreshedForUser || !restoreSettled) return;
    if (guideLinkConsumedRef.current) return;
    guideLinkConsumedRef.current = true;
    const link = consumeGuideDeepLink();
    if (link.open) navigateTo('guides', { videoId: link.videoId, chapter: link.chapter || null });
  }, [user?.email, cacheRefreshedForUser, restoreSettled]);

  // Outlook mail OAuth callback: the backend sends the browser back to the app root
  // with ?microsoft_mail=<status>; land on the Email page (which reads and strips
  // the param) when the user can see it, otherwise stay on the default tab.
  const mailCallbackConsumedRef = useRef(false);
  useEffect(() => {
    if (!user?.email || !cacheRefreshedForUser) return;
    if (mailCallbackConsumedRef.current) return;
    mailCallbackConsumedRef.current = true;
    if (!new URLSearchParams(window.location.search).has('microsoft_mail')) return;
    if (userTeamService.getAccessibleTabs(user.email).includes('email')) {
      setActiveTab('email');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot once login state is ready
  }, [user?.email, cacheRefreshedForUser]);

  useEffect(() => {
    if (activeTab === LEGACY_ADMIN_TAB && user?.email) {
      const fallback = getFirstAccessibleAdminTab(userTeamService.getAccessibleTabs(user.email)) || 'adminTeams';
      setActiveTab(fallback);
    }
  }, [activeTab, user?.email, setActiveTab]);

  useEffect(() => {
    // Deep links / resumed state can still land on a retired page id (e.g. Client
    // Meeting): send it where resolveRetiredTab says (home for Client Meeting).
    if (activeTab === 'implementationChecklist' || activeTab === 'tasks' || RETIRED_TAB_REDIRECTS[activeTab]) {
      const retired = resolveRetiredTab(activeTab);
      setActiveTab(retired.tabName);
      setActiveSection(retired.sectionId ?? null);
    }
    if (LEGACY_ADVISORY_PORTAL_TABS.includes(activeTab)) {
      setActiveTab('advisoryPortal');
    }
    if (activeTab === LEGACY_GA_PORTAL_TAB) {
      setActiveTab('advisoryPortal');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when the front tab's page changes
  }, [activeTab]);

  // When on a client-required tab and selectedClient is cleared, redirect to role default portal
  useEffect(() => {
    if (tabRequiresClient(activeTab) && !selectedClient) {
      setActiveTab(getDefaultTabForUser(user));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run when activeTab or selectedClient change
  }, [activeTab, selectedClient]);

  useEffect(() => {
    if (user || loading) return;
    setRememberMe(getStoredRememberMe());
    setLoginForm({
      email: getStoredRememberMeEmail(),
      password: '',
    });
  }, [user, loading]);

  const handleRememberMeChange = (checked) => {
    setRememberMe(checked);
    if (!checked) {
      setStoredRememberMeEmail(null);
    }
  };

  const handleLogin = async () => {
    if (!loginForm.email || !loginForm.password) return;
    let reloading = false;
    try {
      setLoginSubmitting(true);
      setLoginError('');
      if (typeof window !== 'undefined') {
        localStorage.setItem(REMEMBER_ME_KEY, String(rememberMe));
        if (rememberMe) {
          setStoredRememberMeEmail(loginForm.email);
        } else {
          setStoredRememberMeEmail(null);
        }
      }
      await setPersistence(
        auth,
        rememberMe ? browserLocalPersistence : browserSessionPersistence
      );
      await signInWithEmailAndPassword(auth, loginForm.email, loginForm.password);
      setLastActivityAt();
      setLoginForm({ email: '', password: '' });

      // The login screen may itself be a stale overnight tab. Reload before the
      // app renders so the session starts on the deployed build. No resume state
      // is stashed — a fresh login lands on the role default tab either way.
      if (await shouldReloadAfterLogin()) {
        reloading = true;
        markLoginReloaded();
        window.location.reload();
        return;
      }
    } catch (error) {
      console.error('Login error:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Login failed. Please check your credentials.';
      
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address. Please contact your administrator.';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password. Please try again or use "Forgot Password".';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address format.';
      } else if (error.code === 'auth/user-disabled') {
        errorMessage = 'This account has been disabled. Please contact your administrator.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed login attempts. Please try again later.';
      } else if (error.code === 'auth/invalid-credential') {
        errorMessage = 'Invalid email or password. Please check your credentials.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else if (error.message) {
        errorMessage = `Login failed: ${error.message}`;
      }

      setLoginError(errorMessage);
    } finally {
      // While reloading, keep the button in its loading state rather than
      // flashing back to "Sign In".
      if (!reloading) {
        setLoginSubmitting(false);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !loginSubmitting) {
      handleLogin();
    }
  };

  const handleLogout = async () => {
    try {
      clearLastActivityAt();
      // Deliberate sign-out: let the next sign-in check for a new build again.
      // Not done in the onAuthStateChanged null branch, which also fires when a
      // session fails to restore and would reopen the reload loop.
      clearLoginReloadGuard();
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const userConfigReady = !user?.email || userTeamService.hasUserConfig(user.email);

  if (user && workspaceLoadError && !cacheRefreshedForUser) {
    return (
      <AppLoadingScreen
        error={workspaceLoadError}
        onRetry={() => setWorkspaceRetryNonce((n) => n + 1)}
        onSignOut={handleLogout}
      />
    );
  }

  if (loading || (user && (!cacheRefreshedForUser || !userConfigReady))) {
    return <AppLoadingScreen message={loading ? 'Loading Advicelink…' : 'Loading your workspace…'} />;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-96 p-8">
          <CardContent className="p-0">
            <div className="flex flex-col items-center mb-6">
              <img
                src="/logo.png"
                alt="Advicelink Logo"
                className="h-16 w-auto object-contain mb-4"
              />
              <h2 className="text-2xl font-bold text-center text-foreground">
                {getDisplayAppName()}
              </h2>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="login-email">Email</Label>
                <Input
                  id="login-email"
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => {
                    setLoginError('');
                    setLoginForm(prev => ({ ...prev, email: e.target.value }));
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder="your.email@example.com"
                  aria-invalid={Boolean(loginError)}
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="login-password">Password</Label>
                  <button
                    type="button"
                    className="text-xs text-primary hover:underline shrink-0"
                    onClick={() => setForgotPasswordOpen(true)}
                  >
                    Forgot password?
                  </button>
                </div>
                <Input
                  id="login-password"
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => {
                    setLoginError('');
                    setLoginForm(prev => ({ ...prev, password: e.target.value }));
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder="Password"
                  aria-invalid={Boolean(loginError)}
                />
              </div>
              {loginError ? (
                <div
                  role="alert"
                  className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
                >
                  {loginError}
                </div>
              ) : null}
              <CheckboxField
                id="login-remember-me"
                label="Remember me"
                checked={rememberMe}
                onChange={handleRememberMeChange}
              />
              <Button
                type="button"
                onClick={handleLogin}
                disabled={loginSubmitting}
                className="w-full"
              >
                {loginSubmitting ? 'Signing in...' : 'Sign In'}
              </Button>
            </div>
          </CardContent>
        </Card>
        <ForgotPasswordDialog
          open={forgotPasswordOpen}
          onOpenChange={setForgotPasswordOpen}
          initialEmail={loginForm.email}
        />
      </div>
    );
  }

  const appHeaderValue = {
    user,
    selectedClient,
    getClientDisplayName,
    handleLogout,
  };

  const parentTab = SUB_PAGE_PARENT[activeTab];
  const showParentInBreadcrumb = !!parentTab;

  const headerBreadcrumbCrumbs = (() => {
    const crumbs = [
      {
        key: 'app',
        node: <span className="text-muted-foreground">Advicelink</span>,
        itemClassName: 'hidden md:block',
        separatorClassName: 'hidden md:block',
      },
    ];
    if (showParentInBreadcrumb) {
      crumbs.push({
        key: 'parent',
        node: (
          <BreadcrumbLink asChild>
            <button type="button" onClick={() => handleTabChange(parentTab)}>
              {getPageLabel(parentTab)}
            </button>
          </BreadcrumbLink>
        ),
      });
    }
    const settingsEntry = getSettingsEntryForTab(activeTab);
    if (settingsEntry) {
      // Settings › <entry> (merged entries show their own tabs, not the page id's label).
      crumbs.push({
        key: 'settings',
        node: (
          <BreadcrumbLink asChild>
            <button type="button" onClick={() => settingsEntryTab && guardedNavigate(() => { setSettingsMenuRequest((n) => n + 1); handleNavigate(settingsEntryTab); })}>
              Settings
            </button>
          </BreadcrumbLink>
        ),
      });
      crumbs.push({ key: 'page', node: <BreadcrumbPage>{settingsEntry.label}</BreadcrumbPage> });
      return crumbs;
    }
    crumbs.push({
      key: 'page',
      node: <BreadcrumbPage>{getPageLabel(activeTab)}</BreadcrumbPage>,
    });
    if (activeTab === 'documents' && documentStorageBreadcrumbPath?.length > 0) {
      documentStorageBreadcrumbPath.forEach((segment, i) => {
        const isLast = i === documentStorageBreadcrumbPath.length - 1;
        crumbs.push({
          key: `doc-${i}-${segment}`,
          node: (
            <BreadcrumbPage className={isLast ? 'truncate' : undefined}>
              {segment}
            </BreadcrumbPage>
          ),
          truncate: isLast,
        });
      });
    } else if (
      activeSection &&
      activeTab !== 'documents' &&
      isSectionInSidebar(parentTab ?? activeTab, activeSection)
    ) {
      crumbs.push({
        key: 'section',
        node: (
          <BreadcrumbPage className="truncate">
            {getSectionTitle(parentTab ?? activeTab, activeSection)}
          </BreadcrumbPage>
        ),
        truncate: true,
      });
    }
    return crumbs;
  })();

  // One page per workspace tab, rendered in that tab's client scope. Hidden tabs stay
  // mounted (display:none) so they keep unsaved input, filters and scroll position.
  const isKnownPage = (page) => page === 'clientOverview' || page === 'adviceDates' || page === 'factFind' || page === 'reverseFactFind' || page === 'adviceJourney' || page === 'advice' || page === 'guidedAdvice' || page === 'fileNote' || page === 'fileNotes' || page === 'messages' || page === 'amendments' || page === 'documents' || page === 'eSignatures' || page === 'sendEnvelope' || ADMIN_TAB_SET.has(page) || page === 'advisoryPortal' || page === 'complianceDashboard' || page === 'csvMerge' || page === 'calendar' || page === 'email' || page === 'messagesInbox' || page === 'paraplannerPortal' || page === 'auditLog' || page === 'editRecommendedPortfolio' || page === 'editAlternativePortfolio' || page === 'guides' || page === 'faqs' || page === 'helpDesk' || REPORTS_TAB_SET.has(page);
  const renderTabPage = (tab) => {
    const { page, section } = tab;
    const setters = getTabSetters(tab.id);
    if (page === 'calendar') return null;
    if (!isKnownPage(page)) {
      return (
        <div className="h-full overflow-y-auto">
          <div className="w-full px-4 lg:px-6 xl:px-8 py-8">
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-6">Unknown Tab</h1>
              <p className="text-muted-foreground">Please select a valid tab.</p>
            </div>
          </div>
        </div>
      );
    }
    return page === 'clientOverview' ? (
            <ClientOverviewPage user={user} />
          ) : page === 'adviceDates' ? (
            <AdviceDatesPage user={user} />
          ) : page === 'factFind' ? (
            <FactFind user={user} viewAsRole={viewAsRole} activeTab={page} activeSection={section} onActiveSectionChange={setters.setSection} scrollToSectionId={tab.scrollToSectionId} onScrollRequestFulfilled={setters.onScrollRequestFulfilled} />
          ) : page === 'reverseFactFind' ? (
            <ReverseFactFindPage user={user} viewAsRole={viewAsRole} activeSection={section} onActiveSectionChange={setters.setSection} scrollToSectionId={tab.scrollToSectionId} onScrollRequestFulfilled={setters.onScrollRequestFulfilled} />
          ) : page === 'adviceJourney' ? (
            <AdviceJourneyTab user={user} />
          ) : page === 'advice' ? (
            <Advice user={user} activeTab={page} activeSection={section} onActiveSectionChange={setters.setSection} isFrontTab={tab.id === workspace.activeId} />
          ) : page === 'guidedAdvice' ? (
            <GuidedAdvicePage user={user} activeTab={page} isFrontTab={tab.id === workspace.activeId} onNavigate={handleNavigate} />
          ) : page === 'fileNote' ? (
            <FileNotePage />
          ) : page === 'fileNotes' ? (
            <FileNotesPage user={user} />
          ) : page === 'messages' ? (
            <MessagesPage user={user} />
          ) : page === 'amendments' ? (
            <AmendmentsPage user={user} activeSection={section} onActiveSectionChange={setters.setSection} isFrontTab={tab.id === workspace.activeId} />
          ) : page === 'documents' ? (
            <DocumentStoragePage user={user} onBreadcrumbPathChange={setters.setDocumentStorageBreadcrumbPath} />
          ) : page === 'eSignatures' ? (
            <ESignaturesPage user={user} viewAsRole={viewAsRole} />
          ) : page === 'email' ? (
            <EmailPage user={user} setActiveTab={handleTabChange} />
          ) : page === 'messagesInbox' ? (
            <MessagesInboxPage key={section === 'client' ? 'client' : 'all'} user={user} onNavigate={handleNavigate} initialClientOnly={section === 'client'} />
          ) : page === 'smsBroadcast' ? (
            <BulkSmsPage user={user} />
          ) : page === 'sendEnvelope' ? (
            <SendEnvelopePage user={user} />
          ) : page === 'advisoryPortal' ? (
            <AdvisoryPortal user={user} setActiveTab={handleTabChange} activeSection={section} onActiveSectionChange={setters.setSection} scrollToSectionId={tab.scrollToSectionId} onScrollRequestFulfilled={setters.onScrollRequestFulfilled} searchPrefill={tab.portalSearchPrefill} onSearchPrefillConsumed={setters.onPortalSearchPrefillConsumed} />
          ) : page === 'complianceDashboard' ? (
            <ComplianceDashboardPage user={user} />
          ) : page === 'csvMerge' ? (
            <CsvMergePortal user={user} />
          ) : page === 'paraplannerPortal' ? (
            <ParaplannerPortal user={user} activeSection={section} onActiveSectionChange={setters.setSection} scrollToSectionId={tab.scrollToSectionId} onScrollRequestFulfilled={setters.onScrollRequestFulfilled} searchPrefill={tab.portalSearchPrefill} onSearchPrefillConsumed={setters.onPortalSearchPrefillConsumed} />
          ) : page === 'auditLog' ? (
            <AuditLog user={user} />
          ) : ADMIN_TAB_SET.has(page) ? (
            <SettingsLayout
              activeTab={page}
              canAccess={canAccessNavTab}
              onNavigate={(tab) => guardedNavigate(() => handleNavigate(tab))}
              openMenu={tab.settingsMenuRequest}
            >
              <Suspense fallback={<PageLoadingState />}>
                {renderAdminPage(page, user, { canAccess: canAccessNavTab, onNavigate: (tab) => handleNavigate(tab) })}
              </Suspense>
            </SettingsLayout>
          ) : page === 'guides' ? (
            <GuidesPage user={user} />
          ) : page === 'faqs' ? (
            <FAQsPage />
          ) : page === 'helpDesk' ? (
            <HelpDeskPage user={user} />
          ) : REPORTS_TAB_SET.has(page) ? (
            renderReportsPage(page, user)
    ) : null;
  };

  const workspaceTabPageLabel = (page) => getSettingsEntryForTab(page)?.label ?? getPageLabel(page);

  const exitViewAs = () => {
    sessionStorage.removeItem(VIEW_AS_STORAGE_KEY);
    window.location.href = window.location.pathname;
    window.location.reload();
  };

  return (
    <TooltipProvider delayDuration={300}>
    <VoiceCallProvider user={user}>
    <Toaster richColors closeButton position="top-center" />
    <DocumentWarningPrompt />
    <CommsNotificationSetup
      user={user}
      enabled={Boolean(cacheRefreshedForUser && user?.email)}
    />
    <SupportChatWidget
      user={user}
      enabled={Boolean(cacheRefreshedForUser && user?.email)}
      callSlot={isMarketingViewer ? null : <CallWidget />}
      paraSlot={isMarketingViewer ? null : <ParaButton user={user} enabled={Boolean(cacheRefreshedForUser && user?.email)} />}
    />
    <AppHeaderContext.Provider value={appHeaderValue}>
    <WorkspaceTabsContext.Provider value={workspaceTabsValue}>
    {/* 100dvh: iOS Safari's 100vh runs under its toolbars. Safe-area padding for the notch / home indicator (viewport-fit=cover). */}
    <div className="h-screen h-[100dvh] bg-background flex flex-col overflow-hidden overflow-x-hidden pt-[env(safe-area-inset-top)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]">
      <SidebarProvider className="flex-1 min-h-0 flex">
        <ContextMenuProvider>
        <AppSidebar
          navTree={navTree}
          activeTab={parentTab ?? activeTab}
          activeSection={activeSection}
          onNavigate={(tab, section, opts) => guardedNavigate(() => {
            if (opts?.settings) setSettingsMenuRequest((n) => n + 1);
            handleNavigate(tab, section);
          })}
          clientGroupLabel={selectedClient && getClientDisplayName ? getClientDisplayName() : null}
          settingsTab={settingsEntryTab}
          settingsActive={isSettingsTab(activeTab)}
          onLogoClick={() => {
            const homeTab = viewAsRole
              ? getDefaultTabForViewAsRole(viewAsRole)
              : getDefaultTabForUser(user);
            handleNavigate(homeTab);
          }}
          tabDisabledNoClient={tabDisabledNoClient}
          badgesByTab={sidebarBadgesByTab}
          badgeHintsByTab={sidebarBadgeHintsByTab}
          onOpenInNewTab={(tab, section) => openPageInNewTab(tab, section)}
          showSupportChat={Boolean(cacheRefreshedForUser && user?.email)}
          onExitViewAs={exitViewAs}
          viewAsRole={viewAsRole}
          onViewAsRole={user?.email && userTeamService.getUserRole(user.email) === 'super_admin' ? (roleKey) => {
            sessionStorage.setItem(VIEW_AS_STORAGE_KEY, roleKey);
            window.location.search = `?viewAs=${roleKey}`;
            window.location.reload();
          } : undefined}
        />
        <SidebarInset className="flex-1 min-h-0 flex flex-col">
          <header className="shrink-0 border-b border-border">
            <div className="flex h-14 sm:h-16 items-center gap-1.5 sm:gap-2 px-2 sm:px-4">
            <SidebarTrigger className="-ml-1 shrink-0 h-10 w-10 md:h-7 md:w-7" />
            <Breadcrumb className="flex-1 min-w-0">
              <CollapsingBreadcrumbList crumbs={headerBreadcrumbCrumbs} />
            </Breadcrumb>
            <div className="ml-auto flex min-w-0 max-w-[70%] shrink items-center justify-end">
            {isPhoneHeader ? (
              <WorkspaceTabSwitcher
                tabs={workspace.tabs}
                activeId={workspace.activeId}
                getScope={workspace.getScope}
                pageLabelFor={workspaceTabPageLabel}
                canOpenTab={workspace.canOpenTab}
                onSwitch={workspace.switchTab}
                onClose={workspace.closeTab}
                onNew={openNewWorkspaceTab}
                justOpenedId={justOpenedTabId}
              />
            ) : (
              <WorkspaceTabStrip
                tabs={workspace.tabs}
                activeId={workspace.activeId}
                getScope={workspace.getScope}
                pageLabelFor={workspaceTabPageLabel}
                canOpenTab={workspace.canOpenTab}
                onSwitch={workspace.switchTab}
                onClose={workspace.closeTab}
                onNew={openNewWorkspaceTab}
                onReorder={workspace.reorderTabs}
                justOpenedId={justOpenedTabId}
                onActiveClientClick={getClientBadgePortalTab() ? handleClientBadgeClick : undefined}
              />
            )}
            </div>
            </div>
            <HeaderBannerRow />
            <ResearchLibraryBanner
              enabled={userTeamService.getUserRole(user?.email) === 'super_admin'}
              onOpen={() => handleTabChange('adminPortfolios')}
            />
          </header>
          <div className="flex-1 min-h-0 overflow-hidden flex flex-col" style={{ transform: 'translateZ(0)' }}>
          {/* Calendar: one instance shared by every tab, mounted on first visit, then kept mounted */}
          <div style={{ display: activeTab === 'calendar' ? 'block' : 'none', flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {calendarVisited ? (
              <Suspense fallback={<PageLoadingState />}>
                <CalendarPage user={user} />
              </Suspense>
            ) : null}
          </div>
          {workspace.tabs.map((tab) => (tab.mounted ? (
            <div
              key={tab.id}
              data-workspace-tab={tab.id}
              style={{ display: tab.id === workspace.activeId && tab.page !== 'calendar' ? 'block' : 'none', flex: 1, minHeight: 0, overflow: 'hidden' }}
            >
              <ClientScopeContext.Provider value={workspace.getScope(tab.id)}>
                <Suspense fallback={<PageLoadingState />}>
                  {renderTabPage(tab)}
                </Suspense>
              </ClientScopeContext.Provider>
            </div>
          ) : null))}
          </div>
        </SidebarInset>
        </ContextMenuProvider>
      </SidebarProvider>
    </div>
    </WorkspaceTabsContext.Provider>
    </AppHeaderContext.Provider>
    </VoiceCallProvider>
    </TooltipProvider>
  );
};

export default FinancialAdviserApp;
